// The persistent right rail of a single-player match: the Champion context and
// the RightPanel with all its sim wiring. Extracted from App.tsx (ROADMAP 7.14).

import type { ComponentProps } from 'react';
import { ChampionProvider } from '../champion/ChampionContext.js';
import type { SimWorld } from '../sim/useSimWorld/index.js';
import { RightPanel } from '../ui/RightPanel/index.js';
import type { useSimFeeds } from './useSimFeeds.js';

type RightPanelProps = ComponentProps<typeof RightPanel>;

interface MatchRightRailProps {
  sim: SimWorld;
  /** `sim.snapshot`, already narrowed non-null by App. */
  snapshot: RightPanelProps['snapshot'];
  localPlayerIndex: number;
  feeds: ReturnType<typeof useSimFeeds>;
  selected: RightPanelProps['selected'];
  garrisonCount: RightPanelProps['garrisonCount'];
  buildings: RightPanelProps['buildings'];
  playerColors: RightPanelProps['playerColors'];
  suggestions: RightPanelProps['suggestions'];
  mobileOpen: boolean;
  onDeploy: RightPanelProps['onDeploy'];
  onStartBuilding: RightPanelProps['onStartBuilding'];
  onSetDoctrine: RightPanelProps['onSetDoctrine'];
  onApplySuggestion: RightPanelProps['onApplySuggestion'];
  onClose: RightPanelProps['onClose'];
}

export function MatchRightRail({
  sim,
  snapshot,
  localPlayerIndex,
  feeds,
  selected,
  garrisonCount,
  buildings,
  playerColors,
  suggestions,
  mobileOpen,
  onDeploy,
  onStartBuilding,
  onSetDoctrine,
  onApplySuggestion,
  onClose,
}: MatchRightRailProps) {
  const { dynasty, espionage, heroCaptivity, heroQuests, diplomacy, treatyDesk } = feeds;
  return (
    <ChampionProvider
      snapshot={sim.snapshot}
      tick={sim.tick}
      localPlayerIndex={localPlayerIndex}
      summon={sim.summonLegend}
      activate={sim.activateLegendAbility}
    >
      <RightPanel
        selected={selected}
        snapshot={snapshot}
        localPlayerIndex={localPlayerIndex}
        matchOver={sim.victory !== null}
        garrisonCount={garrisonCount}
        buildings={buildings}
        treasuryIndustry={sim.localTreasury.industry}
        onAttack={sim.attackCity}
        onDeploy={onDeploy}
        onStartBuilding={onStartBuilding}
        onAcceptMerchant={sim.acceptMerchant}
        onDeclineMerchant={sim.declineMerchant}
        dynasty={dynasty}
        espionage={espionage}
        heroCaptivity={heroCaptivity}
        heroQuests={heroQuests}
        diplomacy={diplomacy}
        onProposeTreaty={sim.treaty.propose}
        onDeclareWar={sim.treaty.declareWar}
        onAnswerTreaty={(from, accept) => treatyDesk.answer(from, accept)}
        onAnswerMarriage={(from, accept) =>
          accept ? sim.dynasty.accept(from) : sim.dynasty.decline(from)
        }
        treatyHistory={treatyDesk.history}
        onProposeMarriage={sim.dynasty.propose}
        onDispatchSpy={sim.espionage.dispatchSpy}
        opsActions={sim.espionage}
        onChooseCapture={sim.espionage.chooseCapture}
        onSpyRescue={sim.espionage.spyRescue}
        onRaidRescue={sim.espionage.raidRescue}
        onPayRansom={sim.espionage.payRansom}
        onChooseHeroCaptivity={sim.heroCaptivity.chooseHeroCaptivity}
        onPayHeroRansom={sim.heroCaptivity.payHeroRansom}
        onDispatchHeroQuest={sim.heroQuests.dispatchHeroQuest}
        onSetDoctrine={onSetDoctrine}
        suggestions={suggestions}
        onApplySuggestion={onApplySuggestion}
        onActivateAbility={sim.activateHeroAbility}
        onSelectAbility={sim.selectHeroAbility}
        onSetFormation={sim.setFormation}
        onClose={onClose}
        tick={sim.tick}
        events={sim.events ?? []}
        // TODO(right-panel-victory-projection): sim exposes VictoryProgressState
        // (tick-milestone shape); RightPanel wants VictoryProgress
        // (per-player wonder/domination/faith %). No projection exists yet —
        // OverviewTab renders its empty state until one is added.
        victoryProgress={null}
        localPlayer={sim.localPlayer ?? null}
        playerHudEntries={sim.playerHudEntries}
        playerColors={playerColors}
        mobileOpen={mobileOpen}
      />
    </ChampionProvider>
  );
}
